import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { useHotkeys } from 'react-hotkeys-hook';
import { toast } from 'react-hot-toast';
import ErrorBoundary from '@/components/ui/ErrorBoundary';

// Авторизация
import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import ProtectedModuleRoute from '@/components/ProtectedModuleRoute';

// Компоненты макета
import Layout from '@/components/Layout';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorFallback from '@/components/ui/ErrorFallback';

// Компоненты быстрого ввода
import QuickActionPanel from '@/components/QuickActionPanel';
import QuickFeedModal from '@/components/QuickFeedModal';
import BulkInputModal from '@/components/BulkInputModal';
import OperationTemplates from '@/components/OperationTemplates';
import MobileQuickInput from '@/components/MobileQuickInput';
import FloatingMobileButton from '@/components/FloatingMobileButton';

// Страницы (ленивая загрузка для оптимизации)
const Login = React.lazy(() => import('@/pages/Login'));
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const Stacks = React.lazy(() => import('@/pages/Stacks'));
const StackDetail = React.lazy(() => import('@/pages/StackDetail'));
const Feeding = React.lazy(() => import('@/pages/Feeding'));
const Sales = React.lazy(() => import('@/pages/Sales'));
const Customers = React.lazy(() => import('@/pages/Customers'));
const Biohumus = React.lazy(() => import('@/pages/BioghumusWarehouse'));
const Analytics = React.lazy(() => import('@/pages/Analytics'));
const Settings = React.lazy(() => import('@/pages/Settings'));
const Batches = React.lazy(() => import('@/pages/Batches'));
const BatchDetail = React.lazy(() => import('@/pages/BatchDetail'));
const MergedBatchesArchive = React.lazy(() => import('@/pages/MergedBatchesArchive'));
const Warehouse = React.lazy(() => import('@/pages/Warehouse'));
const Notifications = React.lazy(() => import('@/pages/Notifications'));
const RelayManagement = React.lazy(() => import('@/pages/RelayManagement'));
const Users = React.lazy(() => import('@/pages/Users'));

// Хуки и сервисы
import { useStacks } from '@/hooks/useStacks';
import { useFeedTypes } from '@/hooks/useFeedTypes';
import { createFeedingRecord } from '@/services/feedingService';

function App() {
  // Состояние для быстрых действий
  const [quickPanelVisible, setQuickPanelVisible] = useState(false);
  const [quickFeedModalOpen, setQuickFeedModalOpen] = useState(false);
  const [bulkInputModalOpen, setBulkInputModalOpen] = useState(false);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [mobileInputOpen, setMobileInputOpen] = useState(false);
  const [bulkOperation, setBulkOperation] = useState<'feeding'>('feeding');
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  // Данные для форм
  const { stacks = [] } = useStacks();
  const { feedTypes = [] } = useFeedTypes();

  // Последние использованные значения (из localStorage)
  const [lastUsedValues, setLastUsedValues] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('wormtracker_last_used') || '{}');
    } catch {
      return {};
    }
  });

  // Глобальные горячие клавиши
  useHotkeys('ctrl+shift+q', () => setQuickPanelVisible(!quickPanelVisible), [quickPanelVisible]);
  useHotkeys('ctrl+t', () => setTemplatesModalOpen(true), []);
  useHotkeys('ctrl+m', () => setMobileInputOpen(true), []);
  useHotkeys('f1', (e) => {
    e.preventDefault();
    showHotkeyHelp();
  }, []);

  // Обработчики быстрых действий
  const handleQuickAction = useCallback((actionId: string, data?: any) => {
    switch (actionId) {
      case 'feed':
        setQuickFeedModalOpen(true);
        break;
      
      
      case 'harvest':
        // TODO: Открыть форму сбора биогумуса
        toast.success('Функция сбора биогумуса будет добавлена');
        break;
      
      case 'voice':
        handleVoiceCommand();
        break;
      
      case 'bulk_mode':
        setTemplatesModalOpen(true);
        break;
      
      case 'mobile_mode':
        setMobileInputOpen(true);
        break;
      
      default:
        console.log('Unknown action:', actionId, data);
    }
  }, []);

  // Обработчик голосовых команд
  const handleVoiceCommand = useCallback(() => {
    // TODO: Интеграция с голосовым API
    toast.success('🎤 Голосовые команды будут добавлены в следующем обновлении');
  }, []);

  // Обработчик быстрого кормления
  const handleQuickFeed = useCallback(async (feedingData: any) => {
    try {
      await createFeedingRecord(feedingData);
      
      // Сохраняем последние использованные значения
      const newLastUsed = {
        ...lastUsedValues,
        feeding: {
          stackId: feedingData.stack_id,
          feedTypeId: feedingData.feed_type_id,
          amount: feedingData.amount_kg,
          activity: feedingData.consumption_activity
        }
      };
      
      setLastUsedValues(newLastUsed);
      localStorage.setItem('wormtracker_last_used', JSON.stringify(newLastUsed));
      
      toast.success('✅ Кормление записано успешно!');
      setQuickFeedModalOpen(false);
    } catch (error) {
      console.error('Error creating feeding record:', error);
      toast.error('❌ Ошибка при записи кормления');
    }
  }, [lastUsedValues]);

  // Обработчик массового ввода
  const handleBulkInput = useCallback(async (results: any[]) => {
    try {
      const completedOperations = results.filter(r => r.completed && !r.skipped);
      
      // Обрабатываем каждую операцию
      for (const operation of completedOperations) {
        switch (bulkOperation) {
          case 'feeding':
            await createFeedingRecord({
              stack_id: operation.stackId,
              feed_type_id: operation.data.feedTypeId,
              amount_kg: operation.data.amount,
              consumption_activity: operation.data.activity,
              notes: operation.data.notes
            });
            break;
          
        }
      }
      
      toast.success(`✅ Обработано ${completedOperations.length} операций`);
      setBulkInputModalOpen(false);
    } catch (error) {
      console.error('Error in bulk input:', error);
      toast.error('❌ Ошибка при массовом вводе');
    }
  }, [bulkOperation]);

  // Обработчик применения шаблона
  const handleApplyTemplate = useCallback((template: any) => {
    setSelectedTemplate(template);
    setBulkOperation(template.operation);
    setBulkInputModalOpen(true);
    setTemplatesModalOpen(false);
    
    toast.success(`📋 Применен шаблон: ${template.name}`);
  }, []);

  // Обработчик создания шаблона
  const handleCreateTemplate = useCallback((template: any) => {
    // Шаблоны сохраняются автоматически в компоненте OperationTemplates
    toast.success(`✅ Шаблон "${template.name}" создан`);
  }, []);

  // Показать справку по горячим клавишам
  const showHotkeyHelp = () => {
    const helpText = `
🔥 ГОРЯЧИЕ КЛАВИШИ WORMTRACKER:

📋 НАВИГАЦИЯ:
• Ctrl+1 - Главная страница
• Ctrl+2 - Стеллажи
• Ctrl+3 - Кормление
• Ctrl+4 - Партии червей

⚡ БЫСТРЫЕ ДЕЙСТВИЯ:
• Alt+F - Быстрое кормление
• Alt+M - Быстрые замеры
• Alt+H - Сбор биогумуса
• Alt+V - Голосовая команда

🔄 МАССОВЫЙ ВВОД:
• Ctrl+B - Режим массового ввода
• Ctrl+T - Шаблоны операций
• Ctrl+M - Мобильный интерфейс

🎛️ УПРАВЛЕНИЕ:
• Ctrl+Shift+Q - Показать/скрыть панель
• F1 - Эта справка
• Esc - Закрыть модальные окна

💡 Все горячие клавиши работают глобально!
    `;
    
    alert(helpText.trim());
  };

  return (
    <Router>
      <AuthProvider>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onReset={reset}
              resetKeys={['route']}
            >
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <Routes>
                  {/* Страница входа (публичный маршрут) */}
                  <Route path="/login" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Login />
                    </React.Suspense>
                  } />

                  {/* Защищенные маршруты с Layout */}
                  <Route path="/" element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }>
                  <Route index element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Dashboard />
                    </React.Suspense>
                  } />
                  
                  {/* Стеллажи */}
                  <Route path="stacks" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Stacks />
                    </React.Suspense>
                  } />
                  <Route path="stacks/:id" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <StackDetail />
                    </React.Suspense>
                  } />
                  
                  {/* Кормление */}
                  <Route path="feeding" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Feeding />
                    </React.Suspense>
                  } />
                  
                  {/* Партии червей */}
                  <Route path="batches" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Batches />
                    </React.Suspense>
                  } />
                  <Route path="batches/:id" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <BatchDetail />
                    </React.Suspense>
                  } />

                  {/* Архив объединенных партий */}
                  <Route path="merged-batches" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <MergedBatchesArchive />
                    </React.Suspense>
                  } />

                  {/* Склад */}
                  <Route path="warehouse" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Warehouse />
                    </React.Suspense>
                  } />
                  
                  {/* Продажи */}
                  <Route path="sales" element={
                    <ProtectedModuleRoute module="sales">
                      <React.Suspense fallback={<LoadingSpinner />}>
                        <Sales />
                      </React.Suspense>
                    </ProtectedModuleRoute>
                  } />
                  <Route path="customers" element={
                    <ProtectedModuleRoute module="customers">
                      <React.Suspense fallback={<LoadingSpinner />}>
                        <Customers />
                      </React.Suspense>
                    </ProtectedModuleRoute>
                  } />
                  
                  {/* Биогумус */}
                  <Route path="biohumus" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Biohumus />
                    </React.Suspense>
                  } />
                  
                  {/* Аналитика */}
                  <Route path="analytics" element={
                    <ProtectedModuleRoute module="analytics">
                      <React.Suspense fallback={<LoadingSpinner />}>
                        <Analytics />
                      </React.Suspense>
                    </ProtectedModuleRoute>
                  } />
                  
                  {/* Настройки */}
                  <Route path="settings" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Settings />
                    </React.Suspense>
                  } />
                  
                  {/* Уведомления */}
                  <Route path="notifications" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Notifications />
                    </React.Suspense>
                  } />

                  {/* Управление реле */}
                  <Route path="relays" element={
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <RelayManagement />
                    </React.Suspense>
                  } />

                  {/* Пользователи и группы (только для администраторов) */}
                  <Route path="users" element={
                    <ProtectedModuleRoute module="users">
                      <React.Suspense fallback={<LoadingSpinner />}>
                        <Users />
                      </React.Suspense>
                    </ProtectedModuleRoute>
                  } />
                </Route>

                {/* Редирект на главную для неизвестных маршрутов */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>

              {/* Панель быстрых действий */}
              <QuickActionPanel
                isVisible={quickPanelVisible}
                onToggle={() => setQuickPanelVisible(!quickPanelVisible)}
                onAction={handleQuickAction}
              />

              {/* Модальные окна быстрого ввода */}
              <QuickFeedModal
                isOpen={quickFeedModalOpen}
                onClose={() => setQuickFeedModalOpen(false)}
                onSubmit={handleQuickFeed}
                stacks={stacks}
                feedTypes={feedTypes}
                lastUsed={lastUsedValues.feeding}
              />

              <BulkInputModal
                isOpen={bulkInputModalOpen}
                onClose={() => setBulkInputModalOpen(false)}
                operation={bulkOperation}
                stacks={stacks}
                onSubmit={handleBulkInput}
                template={selectedTemplate}
              />

              <OperationTemplates
                isOpen={templatesModalOpen}
                onClose={() => setTemplatesModalOpen(false)}
                onApplyTemplate={handleApplyTemplate}
                onCreateTemplate={handleCreateTemplate}
                stacks={stacks}
              />

              <MobileQuickInput
                isOpen={mobileInputOpen}
                onClose={() => setMobileInputOpen(false)}
                onSubmit={handleBulkInput}
                stacks={stacks}
              />

              {/* Плавающая кнопка мобильного интерфейса */}
              <FloatingMobileButton
                onClick={() => setMobileInputOpen(true)}
              />
            </div>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App;
